import { useSelector, useDispatch } from "react-redux";
import { useEffect, useRef } from "react";

// Util imports
import urlGenerator from "src/utils/urlGenerator";

// Component imports
import Skeleton from "src/components/elements/Skeleton";
import Error from "./_error";
import CategoriesSidebarListing from "src/components/pages/category/CategoriesSidebarListing";
import FaqsListing from "src/components/pages/category/FaqsListing";
import Paginate from "src/components/elements/pagination/Paginate";
import HeadWrapper from "src/components/layouts/HeadWrapper";
import BreadCrumb from "src/components/layouts/BreadCrumb";

// Redux imports
import { requestFaqCategories } from "src/redux/faqCategories/faqCategories.actions";
import { requestCategoryFaqs } from "src/redux/categoryFaqs/categoryFaqs.actions";

// Style imports
import styles from "src/styles/pages/category.module.scss";
import { useSearchParams } from "next/navigation";

const Category = ({ categorySlug, initialRequestedPage }) => {
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const page = searchParams.get("page");

  // Accessing the redux state with logging for debugging purposes
  const categoriesData = useSelector((state) => {
    return state.faqCategories;
  });

  const requestedPage = parseInt(page, 10) || initialRequestedPage;

  const categoryFaqsData = useSelector((state) => {
    return state.categoryFaqs[categorySlug]?.[requestedPage] || {};
  });

  // Memoizing the category name to prevent re-renders when the category name doesn't change
  const categoryName = categoryFaqsData?.payload?.data?.categoryName
    ? categoryFaqsData.payload.data.categoryName.charAt(0).toUpperCase() +
    categoryFaqsData.payload.data.categoryName.slice(1)
    : '';

  // Preventing duplicate API calls using useRef to track fetched state
  const hasFetchedFaqCategories = useRef(false);

  useEffect(() => {  
    if (!categorySlug || !requestedPage) return; // Prevent API call if categorySlug is empty
  
    // Always call the API when categorySlug or requestedPage changes
    dispatch(
      requestCategoryFaqs({
        params: { slug: categorySlug, page: requestedPage, limit: 10 },
      })
    );
  
}, [categorySlug, requestedPage, dispatch]);

  useEffect(() => {
    if (!hasFetchedFaqCategories.current) {
      dispatch(
        requestFaqCategories()
      );
      hasFetchedFaqCategories.current = true;
    }
  }, []);


  const breadCrumbInfo = [
    { itemName: "Home", itemLink: "/" },
    { itemName: categoryName && categoryName.length > 30 ? categoryName.slice(0, 30).trim() + "..." : categoryName },
  ];

  // If the category FAQs data is not found, show error
  if (categoryFaqsData?.payload?.statusCode === 404) {
    return <Error statusCode={404} />;
  }

  return (
    <>
      <HeadWrapper
        title={categoryFaqsData?.payload?.data?.metatitle || categoryName}
        desc={categoryFaqsData?.payload?.data?.seoDescription || undefined}
        keywords={categoryFaqsData?.payload?.data?.seoKeywords || undefined}
        canonical={`https://support.freecharge.in/${categorySlug}`}
      />
      <div className={styles.wrapper}>
        <BreadCrumb breadCrumbInfo={breadCrumbInfo} />
        <div className={styles.container1}>
          <CategoriesSidebarListing categories={categoriesData?.payload?.data} />
          <div className={styles.list_decs}>
          <div className={styles.helpHead2}>
              <h1 className={`${styles.title_head} text-capitalize`}>
                {categoryName || <Skeleton width={200} />}
              </h1>
            </div>
            <div className={styles.mobile_head}>
              <p className={`${styles.title_head2} text-capitalize`}>
                {categoryName || <Skeleton width={200} />}
              </p>
            </div>
            <div className={styles.color_divider}></div>
            <FaqsListing faqs={categoryFaqsData?.payload?.data} categorySlug={categorySlug} />
          </div>
        </div>

        {categoryFaqsData?.payload?.data?.docs?.length > 0 && (
          <Paginate
            href={urlGenerator.categoryDir()}
            link={urlGenerator.categoryLink}
            mainData={categoryFaqsData.payload.data}
            urlSlug={categorySlug}
          />
        )}
      </div>
    </>
  );
};

// Use getServerSideProps instead of getInitialProps for better SSR handling
export async function getServerSideProps({ query }) {
  return {
    props: {
      categorySlug: query.categorySlug || null,
      initialRequestedPage: parseInt(query.page, 10) || 1
    },
  };
}

export default Category;
